import { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Divider,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	Unstable_Grid2 as Grid
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { useShopDetails } from '../../hooks/use-shop';
import { useAuthContext } from '../../contexts/auth-context';
import { BUSINESS_PROFILE_PRESETS, CREATE_LOCATION, UPDATE_LOCATION } from '../../gql/queries';
import { BusinessProfile, BusinessType } from '../../gql/graphql';
import { OverrideForm, overridesInput, StaffPerLine, STAFF_PER_LINE_LABELS } from '../../utils/business-profile';

interface LocationFormProps {
	data?: {
		_id?: string;
		name?: string;
		address?: {
			city?: string;
			street?: string;
			state?: string;
			zipcode?: string;
		};
		email?: string;
		phone?: string;
		printerName?: string;
		businessType?: BusinessType | null;
		profileOverrides?: {
			staffPerLine?: StaffPerLine | null;
			staffLabel?: string | null;
			servicesEnabled?: boolean | null;
			servicesLabel?: string | null;
		} | null;
	};
	onClose: () => void;
}

/** The override controls' state from a stored location: '' means Default (the preset). */
const overrideForm = (overrides?: LocationFormProps['data'] extends infer D ? (D extends { profileOverrides?: infer O } ? O : never) : never): OverrideForm => ({
	staffPerLine: overrides?.staffPerLine ?? '',
	staffLabel: overrides?.staffLabel ?? '',
	servicesEnabled: overrides?.servicesEnabled === true ? 'on' : overrides?.servicesEnabled === false ? 'off' : '',
	servicesLabel: overrides?.servicesLabel ?? '',
});

export default function EditLocation({ data, onClose }: LocationFormProps) {
	const { shopId, locationId: activeLocationId } = useShopDetails();
	const { setBusinessProfile } = useAuthContext();
	const { data: presetsData } = useQuery(BUSINESS_PROFILE_PRESETS);
	const [overrides, setOverrides] = useState<OverrideForm>(overrideForm(data?.profileOverrides));
	const [formData, setFormData] = useState({
		name: data?.name || '',
		city: data?.address?.city || '',
		street: data?.address?.street || '',
		state: data?.address?.state || '',
		zipcode: data?.address?.zipcode || '',
		email: data?.email || '',
		phone: data?.phone || '',
		printerName: data?.printerName || '',
		// Locations stored before this field existed have none; they are salons.
		businessType: data?.businessType || BusinessType.Salon
	});

	const [createLocation, { loading: creating }] = useMutation(CREATE_LOCATION);
	const [updateLocation, { loading: updating }] = useMutation(UPDATE_LOCATION);

	const isEdit = !!data?._id;
	const loading = creating || updating;

	useEffect(() => {
		if (data) {
			setFormData({
				name: data.name || '',
				city: data.address?.city || '',
				street: data.address?.street || '',
				state: data.address?.state || '',
				zipcode: data.address?.zipcode || '',
				email: data.email || '',
				phone: data.phone || '',
				printerName: data.printerName || '',
				businessType: data.businessType || BusinessType.Salon
			});
			setOverrides(overrideForm(data.profileOverrides));
		}
	}, [data]);

	/* What the chosen kind means with nothing overridden: the Default options name it. */
	const preset: BusinessProfile | undefined = presetsData?.businessProfilePresets?.find(
		(p: { businessType: BusinessType }) => p.businessType === formData.businessType
	)?.profile;
	const updateOverride = <K extends keyof OverrideForm>(key: K, value: OverrideForm[K]) =>
		setOverrides((prev) => ({ ...prev, [key]: value }));
	const labelTooLong = overrides.staffLabel.trim().length > 30 || overrides.servicesLabel.trim().length > 30;

	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
	}, []);

	const handleSubmit = useCallback(async (event: React.FormEvent) => {
		event.preventDefault();

		// Validation
		if (!formData.name.trim()) {
			toast.error('Location name is required');
			return;
		}
		if (!formData.city.trim()) {
			toast.error('City is required');
			return;
		}
		if (!formData.email.trim()) {
			toast.error('Email is required');
			return;
		}
		if (!formData.phone.trim()) {
			toast.error('Phone is required');
			return;
		}
		if (labelTooLong) {
			toast.error('A label can be at most 30 characters');
			return;
		}

		const locationInput = {
			name: formData.name.trim(),
			address: {
				city: formData.city.trim(),
				street: formData.street.trim(),
				state: formData.state.trim(),
				zipcode: formData.zipcode.trim(),
				landmark: '' // Add default value
			},
			email: formData.email.trim(),
			phone: formData.phone.trim(),
			printerName: formData.printerName?.trim() || '',
			businessType: formData.businessType,
			// Every override field is named, null for Default: what the form shows is what is stored (D5).
			profileOverrides: overridesInput(overrides)
		};

		try {
			if (isEdit) {
				// Update existing location - FIX: Pass as ShopInput with location array
				const result = await updateLocation({
					variables: {
						shopId: shopId,
						locationId: data._id,
						location: {
							name: '', // Not used in update, but required by ShopInput type
							location: [locationInput]
						}
					}
				});
				// The admin who saved the location they are working in sees its
				// profile at once; other sessions on their next load (D10).
				if (data._id === activeLocationId) {
					const saved = result.data?.updateLocation?.location?.find((l: { _id?: string | null }) => l._id === data._id);
					setBusinessProfile(saved?.profile);
				}
				toast.success('Location updated successfully');
			} else {
				// Create new location
				await createLocation({
					variables: {
						shop: {
							shopId: shopId,
							location: [locationInput]
						}
					}
				});
				toast.success('Location created successfully');
			}
			onClose();
		} catch (error: any) {
			toast.error(`Failed to ${isEdit ? 'update' : 'create'} location: ${error.message}`);
		}
	}, [formData, overrides, labelTooLong, isEdit, data, shopId, activeLocationId, setBusinessProfile, createLocation, updateLocation, onClose]);

	return (
		<form onSubmit={handleSubmit}>
			<Card>
				<CardHeader
					title={isEdit ? 'Edit Location' : 'Create New Location'}
					subheader={isEdit ? 'Update location details' : 'Add a new location to your shop'}
				/>
				<Divider />
				<CardContent>
					<Grid container spacing={3}>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Location Name"
								name="name"
								onChange={handleChange}
								required
								value={formData.name}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="City"
								name="city"
								onChange={handleChange}
								required
								value={formData.city}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<FormControl fullWidth disabled={loading}>
								<InputLabel>Business type</InputLabel>
								<Select
									label="Business type"
									name="businessType"
									value={formData.businessType}
									onChange={(event) =>
										setFormData((prev) => ({
											...prev,
											businessType: event.target.value as BusinessType
										}))
									}
								>
									<MenuItem value={BusinessType.Salon}>Salon</MenuItem>
									<MenuItem value={BusinessType.FoodDelivery}>Restaurant</MenuItem>
									<MenuItem value={BusinessType.OtherServices}>Other service business</MenuItem>
								</Select>
								<FormHelperText>
									What this location sells. The kind sets the defaults below; change only
									what differs here. Existing services are kept whatever is chosen.
								</FormHelperText>
							</FormControl>
						</Grid>
						{/* Each later profile trait (pricing units, jobs, subjects, appointments,
						    memberships) adds its own control here, defaulting from
						    businessProfilePresets, and its field to overridesInput. */}
						<Grid xs={12}>
							<Typography variant="subtitle2">Order page and services</Typography>
							<Typography variant="body2" color="text.secondary">
								Defaults come from the business type. Change only what differs at this branch.
							</Typography>
						</Grid>
						<Grid xs={12} md={6}>
							<FormControl fullWidth disabled={loading}>
								<InputLabel shrink>Staff on order lines</InputLabel>
								<Select
									label="Staff on order lines"
									notched
									displayEmpty
									value={overrides.staffPerLine}
									onChange={(event) => updateOverride('staffPerLine', event.target.value as StaffPerLine | '')}
								>
									<MenuItem value="">Default{preset ? ` (${STAFF_PER_LINE_LABELS[preset.staffPerLine]})` : ''}</MenuItem>
									{Object.values(StaffPerLine).map((value) => (
										<MenuItem key={value} value={value}>{STAFF_PER_LINE_LABELS[value]}</MenuItem>
									))}
								</Select>
								<FormHelperText>
									Required: every bill line names a staff member. Optional: it may. Off: no staff on bills and no commission page.
								</FormHelperText>
							</FormControl>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="What staff are called"
								value={overrides.staffLabel}
								onChange={(event) => updateOverride('staffLabel', event.target.value)}
								error={overrides.staffLabel.trim().length > 30}
								helperText={overrides.staffLabel.trim().length > 30 ? 'At most 30 characters' : preset ? `Leave empty for the default, "${preset.staffLabel}"` : 'Leave empty for the default'}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<FormControl fullWidth disabled={loading}>
								<InputLabel shrink>Services</InputLabel>
								<Select
									label="Services"
									notched
									displayEmpty
									value={overrides.servicesEnabled}
									onChange={(event) => updateOverride('servicesEnabled', event.target.value as 'on' | 'off' | '')}
								>
									<MenuItem value="">Default{preset ? ` (${preset.servicesEnabled ? 'On' : 'Off'})` : ''}</MenuItem>
									<MenuItem value="on">On</MenuItem>
									<MenuItem value="off">Off</MenuItem>
								</Select>
								<FormHelperText>
									Off hides the services pages and refuses new service lines; existing services and old bills are kept.
								</FormHelperText>
							</FormControl>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="What services are called"
								value={overrides.servicesLabel}
								onChange={(event) => updateOverride('servicesLabel', event.target.value)}
								error={overrides.servicesLabel.trim().length > 30}
								helperText={overrides.servicesLabel.trim().length > 30 ? 'At most 30 characters' : preset ? `Leave empty for the default, "${preset.servicesLabel}"` : 'Leave empty for the default'}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12}>
							<TextField
								fullWidth
								label="Street Address"
								name="street"
								onChange={handleChange}
								value={formData.street}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="State"
								name="state"
								onChange={handleChange}
								value={formData.state}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Zip Code"
								name="zipcode"
								onChange={handleChange}
								value={formData.zipcode}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Email"
								name="email"
								type="email"
								onChange={handleChange}
								required
								value={formData.email}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Phone"
								name="phone"
								onChange={handleChange}
								required
								value={formData.phone}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12}>
							<TextField
								fullWidth
								label="Printer Name (Optional)"
								name="printerName"
								onChange={handleChange}
								value={formData.printerName}
								helperText="Name of the printer configured for this location"
								disabled={loading}
							/>
						</Grid>
					</Grid>
				</CardContent>
				<Divider />
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 2,
						p: 2
					}}
				>
					<Button
						color="inherit"
						onClick={onClose}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button
						color="primary"
						type="submit"
						variant="contained"
						disabled={loading}
					>
						{loading ? 'Saving...' : isEdit ? 'Update Location' : 'Create Location'}
					</Button>
				</Box>
			</Card>
		</form>
	);
}
